import { match } from 'ts-pattern'
import { z } from 'zod'
import { pluralize } from '../../utils/text-utils.ts'
import { BUCKET_COLOR_SWATCHES, type BucketSwatchKey } from './resolution-categorization-palette.ts'

/**
 * The five mutually-exclusive resolution states every analyzed finding carries, in the order the
 * composition bar renders them. Outstanding work runs from most to least done — Fixed, then Fix
 * ready, then Needs attention, then Not triaged — with Dismissed last: it is resolved rather than
 * outstanding, so the design parks it at the far end instead of grouping it with Fixed.
 */
export const ResolutionStateSchema = z.enum(['fixed', 'fixReady', 'acknowledged', 'untriaged', 'dismissed'])
export type ResolutionState = z.infer<typeof ResolutionStateSchema>

export const RESOLUTION_STATES = ResolutionStateSchema.options

export type ResolutionStateMeta = {
  label: string
  /** Count-first tooltip wording, e.g. "8 fixes ready" — keeps the grammar right per state. */
  tipLabel: string
  /** The bucket swatch the state is drawn in, which the Pixee-default buckets inherit. */
  swatch: BucketSwatchKey
  /** The swatch's fill, so chips, segments and funnel rows draw the state in the same colour. */
  color: string
}

const withSwatchFill = (meta: Omit<ResolutionStateMeta, 'color'>): ResolutionStateMeta => ({
  ...meta,
  color: BUCKET_COLOR_SWATCHES[meta.swatch].fill,
})

export const RESOLUTION_META: Record<ResolutionState, ResolutionStateMeta> = {
  fixed: withSwatchFill({ label: 'Fixed', tipLabel: 'fixed', swatch: 'nebula' }),
  fixReady: withSwatchFill({ label: 'Fix ready', tipLabel: 'fixes ready', swatch: 'pulse' }),
  acknowledged: withSwatchFill({ label: 'Needs attention', tipLabel: 'need attention', swatch: 'haze' }),
  untriaged: withSwatchFill({ label: 'Not triaged', tipLabel: 'not triaged', swatch: 'neutralLight' }),
  dismissed: withSwatchFill({ label: 'Dismissed', tipLabel: 'dismissed', swatch: 'neutralDark' }),
}

export type ResolutionCounts = Record<ResolutionState, number>

export type ResolutionPanelData = {
  /** Counts per resolution state. Covers analyzed findings only. */
  resolution: ResolutionCounts
  /** Findings whose analysis is running right now. */
  inProgressCount: number
  /** Findings ingested but not yet analyzed. */
  unanalyzedCount: number
}

/**
 * A table filter driven by the panel. `states` is the union a segment or funnel row stands for and
 * is what identifies the filter; `label` names it for the funnel row and for segment tooltips. The
 * active-filter chips label each underlying outcome separately rather than using this name.
 */
export type ResolutionFilter = {
  label: string
  states: readonly ResolutionState[]
}

/** A count of zero for every state — the starting point before any findings are analyzed. */
export const getEmptyResolutionCounts = (): ResolutionCounts => ({
  fixed: 0,
  fixReady: 0,
  acknowledged: 0,
  untriaged: 0,
  dismissed: 0,
})

export const getAnalyzedTotal = (resolution: ResolutionCounts): number =>
  RESOLUTION_STATES.reduce((total, state) => total + resolution[state], 0)

/** Findings that still need someone to do something. Fixed and Dismissed are done. */
export const getActionableTotal = (resolution: ResolutionCounts): number =>
  resolution.fixReady + resolution.acknowledged + resolution.untriaged

export const getResolvedTotal = (resolution: ResolutionCounts): number => resolution.fixed + resolution.dismissed

export const toPercentage = ({ count, total }: { count: number; total: number }): number =>
  total === 0 ? 0 : (count / total) * 100

/** The filter a single resolution state stands for, labelled with that state's name. */
export const toStateFilter = (state: ResolutionState): ResolutionFilter => ({
  label: RESOLUTION_META[state].label,
  states: [state],
})

/** Order-insensitive set equality, the one definition of "same values" in this folder. */
export const areSameValues = <TValue extends string>(a: readonly TValue[], b: readonly TValue[]): boolean =>
  a.length === b.length && a.every(value => b.includes(value))

export const areFiltersEqual = (a: ResolutionFilter | undefined, b: ResolutionFilter | undefined): boolean => {
  if (!a || !b) {
    return a === b
  }
  return areSameValues(a.states, b.states)
}

/**
 * The aggregate roll-ups the funnel offers as filters, alongside the five single states.
 *
 * "Pixee can fix" is deliberately absent: without an API filter for unmerged patches (ISS-8781) its
 * selection is identical to Fix ready's, so it could never win the match in
 * `toActiveResolutionFilter` and clicking it would light up the wrong row. It renders as a
 * non-clickable roll-up until the API can tell the two apart.
 */
export const TRIAGED_FILTER: ResolutionFilter = {
  label: 'Triaged',
  states: ['fixed', 'fixReady', 'acknowledged', 'dismissed'],
}

export const VALIDATED_FILTER: ResolutionFilter = {
  label: 'Validated',
  // Fixed belongs here: a merged fix was validated before it was written. Naming it explicitly
  // does not widen the selection — the union of the three states already left patch status
  // unconstrained, so merged-patch findings matched regardless — but it keeps the states this
  // filter declares in step with the findings it returns and with the funnel's Validated count.
  states: ['fixed', 'fixReady', 'acknowledged'],
}

/**
 * Fully analyzed findings, in the All findings quick view. It names no states: the five states
 * partition exactly these findings, so the hook expresses it as the table's `analyzed` filter
 * rather than as a union of state selections, which would also need every classification.
 *
 * Not among the candidates below, which are recognised from the classification, fix and patch
 * selections alone; the hook recognises this one from the analysis filter.
 */
export const ANALYZED_FILTER: ResolutionFilter = { label: 'Analyzed', states: [] }

/**
 * Every filter the panel can apply. Used to recognise which one the findings table is currently
 * filtered by, so the panel highlights it even when the filter came from somewhere else — a
 * persisted filter restored on load, say, rather than a click in this session.
 */
export const RESOLUTION_FILTER_CANDIDATES: ResolutionFilter[] = [
  ...RESOLUTION_STATES.map(toStateFilter),
  TRIAGED_FILTER,
  VALIDATED_FILTER,
]

export type NextAction = {
  count: number
  /** Reads after the count, e.g. "13 fixes ready to apply". */
  label: string
  state: ResolutionState
  filter: ResolutionFilter
}

/**
 * The single most valuable thing to do next, in priority order: apply ready fixes,
 * then triage, then work the findings needing manual remediation. Undefined when there
 * is no outstanding work.
 */
export const getNextAction = (resolution: ResolutionCounts): NextAction | undefined =>
  match(resolution)
    .when(
      ({ fixReady }) => fixReady > 0,
      ({ fixReady }) => ({
        count: fixReady,
        label: `${pluralize(fixReady, 'fix', 'fixes')} ready to apply`,
        state: 'fixReady' as const,
        filter: toStateFilter('fixReady'),
      })
    )
    .when(
      ({ untriaged }) => untriaged > 0,
      ({ untriaged }) => ({
        count: untriaged,
        label: `${pluralize(untriaged, 'finding needs', 'findings need')} triage`,
        state: 'untriaged' as const,
        filter: toStateFilter('untriaged'),
      })
    )
    .when(
      ({ acknowledged }) => acknowledged > 0,
      ({ acknowledged }) => ({
        count: acknowledged,
        label: `${pluralize(acknowledged, 'finding needs', 'findings need')} manual remediation`,
        state: 'acknowledged' as const,
        filter: toStateFilter('acknowledged'),
      })
    )
    .otherwise(() => undefined)
